import styled from '@emotion/styled';
import {useNavigate} from 'react-router-dom';
import analytics from 'shared/utility/analytics';
import {PrimaryOutlineButton} from 'shared/buttons';
import {ConversieButtons} from 'shared/conversie';
import useMedia from 'shared/hooks/useMedia';
import BaseRow from './BaseRow';
import TableRowRateCells from './TabelRowRateCells';
import {triggerScroll} from 'shared/helpers';
import {getParamsFromState} from '../../../../shared/helpers';
import {whiteListUrlParams} from '../../config';
import {RESULTS_LIMIT} from '../../constants';
import {RadioCheck} from 'shared/components';
import SortingLabels from './SortingLabels';

const TableRow = styled(BaseRow)(props => ({
  display: 'flex',
  alignItems: 'center',
  height: '48px',
  border: props.selectedTopResult ? `2px solid ${props.theme.primaryColor}` : null,
  [`@media ${props.theme.mobile}`]: {
    position: 'relative',
    flexDirection: 'column',
    boxSizing: 'border-box',
    border: '1px solid',
    borderColor: props.theme.borderColor,
    maxHeight: '108px',
    height: '100%',
    marginBottom: '8px',
    transition: 'max-height 200ms',
    overflow: 'hidden',
    ...openRowOnMobile(props),
  },
}));

const openRowOnMobile = props => {
  if (props.openOnMobile) {
    return {
      maxHeight: '276px',
      borderColor: 'transparent',
      boxShadow: '0 0 16px rgba(0, 0, 0, 0.16);',
    };
  }
};

const ClickToLoanpartCells = styled.div`
  box-sizing: border-box;
  flex: 600px 8 0;
  display: flex;
  height: 100%;
  cursor: pointer;
  @media ${props => props.theme.mobile} {
    width: 100%;
    flex: 108px 0 0;
  }
`;

const ClickToActionCell = styled.div(props => ({
  flex: '152px 1 0',
  boxSizing: 'border-box',
  padding: '0 16px',
  button: { height: '32px !important' },
  [`@media ${props.theme.mobile}`]: {
    display: 'block',
    margin: '0 24px 24px',
    width: '100%',
    flex: 'auto 0 0',

    button: { height: '40px !important' },
    '> * + *': { marginTop: 16 },
  },
}));

const LabelInformation = styled.div`
  display: flex;
  align-items: center;
  flex: 280px 3 1;

  @media ${props => props.theme.mobile} {
    flex: 50% 0 0;
    flex-wrap: wrap;
    box-sizing: border-box;
    padding: 24px 8px 24px 24px;
  }
`;

const Logo = styled.div`
  box-sizing: border-box;
  flex: 88px 0 0;
  padding-left: 16px;

  img {
    display: block;
    margin: 0 auto;
    max-height: 32px;
    max-width: 72px;

    @supports (object-fit: contain) {
      width: 72px;
      height: 32px;
      object-fit: contain;
      object-position: center;
    }
  }

  @media ${props => props.theme.mobile} {
    flex: 50% 1 0;

    img {
      max-width: 100%;
      margin: 0;
      @supports (object-fit: contain) {
        width: 100%;
        object-position: left;
      }
    }
  }
`;

const Label = styled.span`
  flex: 5 1;
  width: 140px;
  max-height: 32px;

  display: block;
  box-sizing: border-box;
  padding-left: 16px;

  color: ${({ theme }) => theme.primaryColor};
  font-size: 12px;
  line-height: 16px;

  overflow: hidden;
  text-overflow: ellipsis;

  @media ${props => props.theme.mobile} {
    flex: 100% 0 0;
    width: 114px;
    margin-top: 12px;
    padding: 0;
    white-space: nowrap;

    font-size: 14px;
    line-height: 16px;
  }
`;

const TableRows = props => {
  const {
    data,
    isPreferred,
    remainder,
    type,
    input,
    output,
    app,
    framed,
    sync,
    settings,
    mortgageTypeLabelValue,
  } = props;
  const { isFlexFront, showLeadButton } = settings;
  const base = framed ? `/${app}/framed` : `/${app}`;
  const { onMobile } = useMedia();
  const navigate = useNavigate();

  const limit = isPreferred ? RESULTS_LIMIT : RESULTS_LIMIT - remainder;
  const rows = input.showAllResults ? data : data.slice(0, limit);

  return rows.map((result, index) => {
    const openOnMobile = input.selectedLabel === `${type}${index}`;
    const selectedFrts = result?.frts?.find(el => el.frt === input.sorted_frt);

    const navigateToInterestSheet = () => {
      analytics.send('Navigeer', `/interest-sheet/${result.label_id}`);
      navigate(`${base}/interest-sheet/${result.label_id}`);
      triggerScroll();
    };

    return (
      <TableRow
        index={index}
        key={result.label_id}
        selectedTopResult={input.label_id === result.label_id}
        openOnMobile={openOnMobile}
      >
        <ClickToLoanpartCells
          onClick={() => {
            if (onMobile) {
              if (input.selectedLabel === `${type}${index}`) {
                sync('selectedLabel', null);
              } else {
                sync('selectedLabel', `${type}${index}`);
              }
            } else {
              navigateToInterestSheet();
            }
          }}
        >
          <LabelInformation>
            {onMobile && <RadioCheck active={openOnMobile} css={{ margin: '0 !important' }} />}
            <Logo>
              <img
                  src={result.logo_url}
                  alt={result.mortgage_provider_name}
                  title={result.mortgage_provider_name}
              />
            </Logo>
            <Label title={result.label_name}>
              {result.label_name}
            </Label>
            {!onMobile && <SortingLabels {...{ isPreferred, isFlexFront, input, result }} />}
          </LabelInformation>
          <TableRowRateCells
            rates={result.frts}
            input={input}
            openOnMobile={openOnMobile}
            isPreferred={isPreferred || (isFlexFront && result.collaboration)}
          />
        </ClickToLoanpartCells>
        <ClickToActionCell>
          <ConversieButtons
            settings={settings}
            app={app}
            renderAs={onMobile ? 'PrimaryButton' : 'PrimaryOutlineButton'}
            onClick={() => sync('label_id', result.label_id)}
            urlLeadFormParams={getParamsFromState(
              { ...input, ...output, ...result, ...selectedFrts, mortgageTypeLabelValue },
              whiteListUrlParams
            )}
          />
          {(onMobile || !showLeadButton) && (
            <PrimaryOutlineButton onClick={navigateToInterestSheet}>Toon renteblad</PrimaryOutlineButton>
          )}
        </ClickToActionCell>
        {onMobile && (result.has_auto_risk_class_reduction || result.collaboration) && (
          <SortingLabels {...{ isPreferred, isFlexFront, input, result }} />
        )}
      </TableRow>
    );
  });
};

export default TableRows;
